/**
 * Offline + interruption policy (traceability P128).
 *
 * The sim never sees wall-clock time; this module is the single place where
 * real elapsed time becomes a bounded, deterministic tick count. Policy v0:
 * - Rate: one tick per 90 real seconds (the 1x rate, Matthew 2026-10-22
 *   pacing; catch-up ignores the session's set speed so a paused game still
 *   accrues the same away time).
 * - Cap: 7 in-game days (168 ticks). A long-closed tab resumes with bounded
 *   work and bounded economy drift; retune only here.
 * The bridge saves on pagehide and on visibilitychange->hidden, so an
 * interrupted session (call, app switch) resumes from at most seconds ago.
 */

/** 1 tick = 1 in-game hour; the 1x speed runs one tick per 90 real seconds. */
export const MS_PER_TICK_1X = 90_000;

/** Maximum ticks applied on resume, however long the app was closed. */
export const OFFLINE_CATCHUP_MAX_TICKS = 7 * 24; // 7 in-game days

export interface OfflineCatchup {
  /** Whole real seconds between save and resume. */
  realSeconds: number;
  /** Ticks actually applied (after the cap). */
  ticks: number;
  /** True when the raw away time exceeded the cap. */
  capped: boolean;
}

export function computeOfflineCatchup(savedAtMs: number, nowMs: number): OfflineCatchup {
  if (!Number.isFinite(savedAtMs) || !Number.isFinite(nowMs) || nowMs <= savedAtMs) {
    return { realSeconds: 0, ticks: 0, capped: false };
  }
  const elapsedMs = nowMs - savedAtMs;
  // truncation === floor for non-negative values in int32 range
  const realSeconds = (elapsedMs / 1000) | 0;
  const rawTicks = (elapsedMs / MS_PER_TICK_1X) | 0;
  const capped = rawTicks > OFFLINE_CATCHUP_MAX_TICKS;
  return { realSeconds, ticks: capped ? OFFLINE_CATCHUP_MAX_TICKS : rawTicks, capped };
}
